import React, { memo, useCallback, useState } from 'react';
import {
  List,
  Datagrid,
  DateField,
  DeleteButton,
  Filter,
  FunctionField,
  TextField,
  TextInput,
  useNotify,
  usePermissions,
  useRefresh,
} from 'react-admin';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import roles from '../../constants/roles';
import imageUrl from '../../utils/imageUrl';
import uploadService from '../../utils/upload';

// The API rejects anything that is not a jpg/png/gif/webp/avif (magic-number check in
// FileController.upload), so keep the file picker to the same set.
const ACCEPTED_TYPES = 'image/jpeg,image/png,image/gif,image/webp,image/avif';

export const formatFileSize = (bytes) => {
  if (typeof bytes !== 'number' || Number.isNaN(bytes)) {
    return '';
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

/**
 * Uploads the picked files one by one to POST /api/file. Returns how many landed in S3
 * plus the names that did not, so the caller can report both in a single notification.
 */
export const uploadImages = async (files, upload, onProgress = () => {}) => {
  const failed = [];
  let uploaded = 0;

  for (let index = 0; index < files.length; index += 1) {
    const file = files[index];
    onProgress({ current: index + 1, total: files.length, percent: 0 });

    const formData = new FormData();
    formData.append('file', file);

    try {
      await upload(formData, (progressEvent) => {
        const percent = progressEvent.total
          ? Math.round((progressEvent.loaded / progressEvent.total) * 100)
          : 0;
        onProgress({ current: index + 1, total: files.length, percent });
      });
      uploaded += 1;
    } catch (err) {
      failed.push(`${file.name}${err?.message ? ` (${err.message})` : ''}`);
    }
  }

  return { uploaded, failed };
};

const ListImagesFilter = (props) => (
  <Filter {...props}>
    <TextInput label="Search" source="search" alwaysOn />
  </Filter>
);

const ListImages = (props) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null);
  const notify = useNotify();
  const refresh = useRefresh();
  const { permissions } = usePermissions();

  const canDelete = permissions === roles.superAdmin || permissions === roles.admin;

  const handleFilesUpload = useCallback(
    async (event) => {
      const files = Array.from(event.target.files || []);
      // Reset the input so picking the same file again still fires a change event.
      event.target.value = '';
      if (!files.length) {
        return;
      }

      setUploading(true);
      setProgress({ current: 1, total: files.length, percent: 0 });

      const { uploaded, failed } = await uploadImages(files, uploadService, setProgress);

      setUploading(false);
      setProgress(null);

      if (uploaded) {
        notify(`${uploaded} image${uploaded > 1 ? 's' : ''} uploaded`, { type: 'success' });
        refresh();
      }
      if (failed.length) {
        notify(`Upload failed: ${failed.join(', ')}`, { type: 'error', autoHideDuration: 8000 });
      }
    },
    [notify, refresh],
  );

  const copyToClipboard = useCallback(
    async (text, label) => {
      try {
        await navigator.clipboard.writeText(text);
        notify(`${label} copied to clipboard`, { type: 'info' });
      } catch (err) {
        notify(`Could not copy ${label}: ${text}`, { type: 'warning', autoHideDuration: 8000 });
      }
    },
    [notify],
  );

  const uploadLabel = () => {
    if (!uploading) {
      return 'Upload Images';
    }
    if (progress && progress.total > 1) {
      return `Uploading ${progress.current}/${progress.total} (${progress.percent}%)`;
    }
    return `${progress ? progress.percent : 0}% Uploaded`;
  };

  return (
    <>
      <input
        accept={ACCEPTED_TYPES}
        style={{ display: 'none' }}
        id="upload-image-button"
        type="file"
        multiple
        onChange={handleFilesUpload}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <label htmlFor="upload-image-button">
          <Button variant="contained" color="primary" component="span" disabled={uploading}>
            {uploadLabel()}
          </Button>
        </label>
        <Box sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
          JPG, PNG, GIF, WebP or AVIF. Copy a file name to use it as a program, product or session
          picture.
        </Box>
      </Box>
      <List
        {...props}
        resource="file"
        exporter={false}
        bulkActions={false}
        empty={<Box sx={{ p: 3, color: 'text.secondary' }}>No images uploaded yet.</Box>}
        perPage={25}
        sort={{ field: 'createdAt', order: 'DESC' }}
        filters={<ListImagesFilter />}
      >
        <Datagrid bulkActionButtons={false}>
          <FunctionField
            label="Preview"
            sortable={false}
            render={(record) => (
              <img
                src={imageUrl(record.fileName, '160x90')}
                alt={record.originalName}
                style={{
                  width: 80,
                  height: 45,
                  objectFit: 'cover',
                  display: 'block',
                  // Keeps the column from collapsing when a thumbnail fails to load.
                  backgroundColor: '#eceff1',
                  borderRadius: 4,
                }}
              />
            )}
          />
          <TextField label="Original Name" source="originalName" sortable={false} />
          <FunctionField
            label="File Name"
            sortable={false}
            render={(record) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <span>{record.fileName}</span>
                <Tooltip title="Copy file name">
                  <IconButton
                    size="small"
                    onClick={() => copyToClipboard(record.fileName, 'File name')}
                  >
                    <ContentCopyIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          />
          <FunctionField
            label="Size"
            sortable={false}
            render={(record) => formatFileSize(record.size)}
          />
          <DateField label="Created At" source="createdAt" showTime sortable />
          <FunctionField
            label="Actions"
            sortable={false}
            render={(record) => (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Tooltip title="Open full size">
                  <IconButton
                    size="small"
                    href={imageUrl(record.fileName, '1600x1600')}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <OpenInNewIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
                {canDelete && <DeleteButton resource="file" record={record} redirect={false} />}
              </Box>
            )}
          />
        </Datagrid>
      </List>
    </>
  );
};

export default memo(ListImages);
